import React, { useEffect, useMemo, useState } from "react";
import RadioOption from "../../components/Inputs/RadioOption";
import AuthLabeledTextInput from "../../components/Inputs/AuthLabeledTextInput";
import { useQuery } from "@tanstack/react-query";
import LoadingIcon from "../../components/Loaders/LoadingIcon";
import { Pricing } from "@mysnowscape/snow-lib";
const AddOnPricing = Pricing.AddOnPricing;
const ChoosePlanInput = function ({
  name,
  onPlanChange,
  value = "team-sub",
  requiredUsers = 0,
  requiredCompanies = 0,
  defaultExtraUsers = 0,
  defaultExtraCompanies = 0,
  highlightPlan = null,
  defaultShowCustomizePlan = true,
  excludePlans = [],
}: {
  name: string;
  onPlanChange: (newPlan: string) => void;
  value?: string;
  requiredUsers?: number;
  requiredCompanies?: number;
  defaultExtraUsers?: number;
  defaultExtraCompanies?: number;
  highlightPlan?: string | null;
  defaultShowCustomizePlan?: boolean;
  excludePlans?: string[];
}) {
  const [extraUsers, setExtraUsers] = useState(defaultExtraUsers);
  const [extraCompanies, setExtraCompanies] = useState(defaultExtraCompanies);
  const [showCustomizePlan, setShowCustomizePlan] = useState(
    defaultShowCustomizePlan,
  );
  const { data, isLoading } = useQuery<{ plans: Pricing.PlanDetails[] }>({
    queryKey: ["stripe/pricing"],
  });
  const plans =
    data?.plans.filter((plan) => !excludePlans?.includes(plan.id)) ?? [];
  const [selectedPlan, setSelectedPlan] = useState<Pricing.PlanDetails | null>(
    null,
  );
  const isParkPlanSelected = useMemo<boolean>(
    () =>
      selectedPlan != null &&
      selectedPlan.name.toLocaleLowerCase().includes("park"),
    [selectedPlan],
  );
  useEffect(() => {
    if (selectedPlan == null && plans.length > 0) {
      const match =
        plans.find((p) => p.id == value) ?? plans.find((p) => p.default);
      if (match) {
        setSelectedPlan(match);
      }
    }
  }, [plans]);
  useEffect(() => {
    if (isParkPlanSelected) {
      setExtraCompanies(0);
      setExtraUsers(0);
    }
  }, [isParkPlanSelected]);
  if (isLoading) {
    return <LoadingIcon />;
  }
  return (
    <div data-testid="choose-plan-input">
      {plans.map((plan) => (
        // @ts-expect-error: not going to define all the props that are not required. Remove once it's typescript.
        <RadioOption
          key={plan.id}
          name={name ?? "plan"}
          value={plan.id}
          // defaultChecked={value ? plan.id === value : plan.default ?? false}
          checked={plan.id === selectedPlan?.id}
          onChange={(value: string) => {
            console.log("value changed", value);
            setSelectedPlan(plan);
            if (typeof onPlanChange === "function") {
              onPlanChange(value);
            }
          }}
          disabled={
            Number(plan.allowances.authorizedCompanies) +
              Number(extraCompanies) <
              requiredCompanies ||
            Number(plan.allowances.authorizedUsers) + Number(extraUsers) <
              requiredUsers
          }
        >
          <div
            className={`inline-grid grid-rows-2 lg:grid-rows-1 lg:grid-cols-3 lg:gap-2  ${highlightPlan === plan.id ? "font-bold" : ""}`}
          >
            <span className="flex gap-2">
              <span className="">{plan.name}</span>
              <span>${plan.price} / month</span>
            </span>
            <span className="">
              {plan.allowances.authorizedCompanies}{" "}
              {plan.allowances.authorizedCompanies > 1
                ? "companies"
                : "company"}
              , {plan.allowances.authorizedUsers}{" "}
              {plan.allowances.authorizedUsers > 1 ? "users" : "user"}
            </span>
            <span className="hidden lg:inline">
              {plan.allowances.optimizations} route optimizations / month,{" "}
              {plan.allowances.siteMaps} site maps
            </span>
          </div>
        </RadioOption>
      ))}
      {showCustomizePlan ? (
        <>
          <div data-testid="add-ons" className="flex gap-2 max-w-[400px]">
            <AuthLabeledTextInput
              label={`Extra Users $${AddOnPricing.extra_users.price}`}
              name="extra_users"
              value={extraUsers}
              setValue={(v) => setExtraUsers(Number(v))}
              numbersOnly={true}
              disabled={isParkPlanSelected}
            />
            <AuthLabeledTextInput
              label={`Extra Companies $${AddOnPricing.extra_companies.price}`}
              name="extra_companies"
              value={extraCompanies}
              setValue={(v) => setExtraCompanies(Number(v))}
              numbersOnly={true}
              disabled={isParkPlanSelected}
            />
          </div>
          <p className=" flex flex-col">
            <span data-testid="total-users" className="flex justify-between">
              <span>Total Users:</span>
              <span>
                {selectedPlan?.allowances.authorizedUsers ?? "0"} +{" "}
                {extraUsers ?? "0"} ={" "}
                {Number(selectedPlan?.allowances.authorizedUsers ?? 0) +
                  Number(extraUsers ?? 0)}
              </span>
            </span>
            <span
              data-testid="total-companies"
              className="flex justify-between"
            >
              <span>Total Companies:</span>
              <span>
                {selectedPlan?.allowances.authorizedCompanies ?? "0"} +{" "}
                {extraCompanies ?? "0"} ={" "}
                {Number(selectedPlan?.allowances.authorizedCompanies ?? 0) +
                  Number(extraCompanies ?? 0)}
              </span>
            </span>
          </p>
          <span
            data-testid="total-cost"
            className="flex justify-between font-bold"
          >
            <span>Total Monthly Cost:</span>
            <span>
              $
              {Math.round(
                (Number(selectedPlan?.price ?? 0) +
                  Number(extraUsers ?? 0) * AddOnPricing.extra_users.price +
                  Number(extraCompanies ?? 0) *
                    AddOnPricing.extra_companies.price) *
                  100,
              ) / 100}
            </span>
          </span>
        </>
      ) : (
        <>
          <button
            className="text-sm pl-[32px] relative top-[-5px]"
            onClick={() => setShowCustomizePlan(true)}
          >
            <span className="underline">Customize Your Plan</span> (extra users
            / companies)
          </button>
        </>
      )}
    </div>
  );
};

function suggestAPlan(
  plans: Pricing.PlanDetails[],
  requiredUsers: number,
  requiredCompanies: number,
) {
  return plans.find(
    (p) =>
      p.allowances.authorizedCompanies >= requiredCompanies &&
      p.allowances.authorizedUsers >= requiredUsers,
  );
}

function buildPlanFromForm(submitEvent: Event, plans: Pricing.PlanDetails[]) {
  let form = submitEvent.target as HTMLFormElement;
  let plan = plans.find((p) => p.id === (form.plan as HTMLInputElement).value);
  console.log("plan123", plans, plan, form);
  let extra_users = Number(form.extra_users?.value ?? 0);
  let extra_companies = Number(form.extra_companies?.value ?? 0);
  return {
    plan,
    extra_users,
    extra_companies,
  };
}

export default ChoosePlanInput;
export { suggestAPlan, buildPlanFromForm };
